import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { UseFormReturn } from 'react-hook-form';
import type { WizardFormData } from '@/modules/inventory/components/boat-form/types/wizard.types';
import type { DraftSyncReturn } from '@/modules/inventory/components/boat-form/hooks/useWizardDraftSync';
import type { BoatFormData, InventoryMediaItem } from '@/modules/inventory/types/boat.types';
import { fullWizardSchema } from '@/modules/inventory/components/boat-form/schemas/wizard-schemas';
import { buildBoatPayload } from '@/modules/inventory/components/boat-form/utils/buildBoatPayload';
import { WIZARD_FIELD_LABELS } from '@/modules/inventory/components/boat-form/utils/fieldLabels';
import { useStatuses } from '@/modules/inventory/hooks/useStatuses';
import { useCan } from '@/modules/tenant/hooks/useTenantAuth';
import type { SaveOutcome } from '@/modules/inventory/services/boatSave';
import type { ScalarValue } from '@/modules/inventory/utils/boat-changes';

// ---------------------------------------------------------------------------
// DI interface
// ---------------------------------------------------------------------------

export interface CanvasBoatActionsDeps {
  methods: Pick<UseFormReturn<WizardFormData>, 'getValues' | 'setError' | 'trigger'>;
  draftSync: Pick<DraftSyncReturn, 'hasServerDraft' | 'boatId' | 'createDraft' | 'resetDraft'>;
  /**
   * #1177 (spec §3.1) — the canvas's changes-only save (useCanvasAutoSave.saveNow).
   * `extra` keys are sent whatever changed (the finalize status_id).
   */
  saveChanges: (options?: { extra?: Record<string, ScalarValue> }) => Promise<SaveOutcome | null>;
  mediaUpload: {
    clear: () => void;
    loadExisting: (images: InventoryMediaItem[], primaryImage?: InventoryMediaItem | null) => void;
  };
  createBoat: (data: BoatFormData) => Promise<{ id?: string | number }>;
  /** E5c — persists genuinely-new evolving-field values as provisional terms. */
  createPendingVocabularyTerms: (values: Record<string, unknown>) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (boatId: string) => void;
  setAutoSaveEnabled: (v: boolean) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Orchestration hook for the LiveFillCanvas footer actions.
 *
 * Mirrors AddBoatWizard's handleCreate / handleSaveDraft / handleCreateDraft
 * pattern. Key deviation from the wizard:
 *  - In the no-draft handleCreate path the new boat id comes from the
 *    `createBoat` return value (String(created.id)) since draftSync.boatId
 *    is null, so direct-create still fires onSuccess with the right id.
 *  - "Save draft" also bootstraps the server draft when none exists yet,
 *    which then unlocks autosave + market-band.
 */
export function useCanvasBoatActions(deps: CanvasBoatActionsDeps) {
  const { methods, draftSync, mediaUpload, createBoat, createPendingVocabularyTerms, onOpenChange, onSuccess, setAutoSaveEnabled, saveChanges } = deps;
  // #1408 (D-08) — the direct create sends no money for a viewer without finance.cost.view.
  const canViewCost = useCan('finance.cost.view');

  // ---------------------------------------------------------------------------
  // Status resolution
  //
  // DOMAIN MODEL — two completely independent axes, do NOT conflate:
  //
  //   1. Internal status (status_id → InventoryStatus): operational/sales state.
  //      Values: draft | available | in-stock | sold | reserved | on_order …
  //      "draft" is the ONLY system status and exists ONLY during creation so the
  //      form can be pre-filled from uploaded documents with relaxed validation.
  //      Once a boat is finalized it must leave Draft — staying Draft means
  //      BoatPublished never fires and AI enrichment + packet-document splitting
  //      never run. The "available" status is the default finalized state.
  //
  //   2. website_status (published | disabled): WooCommerce/website visibility.
  //      This is a separate toggle and is what actually controls pushing the
  //      boat to the website. Draft does NOT control website push.
  //
  // TanStack Query deduplicates the request — CanvasFormSections already calls
  // useStatuses(), so this call hits the shared cache, never the network again.
  // ---------------------------------------------------------------------------
  const { data: statusesResponse } = useStatuses();
  const { draftStatusId, availableStatusId } = useMemo(() => {
    const items = statusesResponse?.items ?? [];
    return {
      draftStatusId: items.find((s) => s.is_system === true && s.value === 'draft')?.id ?? null,
      availableStatusId: items.find((s) => s.value === 'available')?.id ?? null,
    };
  }, [statusesResponse]);

  // Guards against double-submit on both the publishDraft (PATCH) path and the
  // createBoat (POST) path. `isCreating` from useCreateBoat only covers the
  // direct-POST mutation; `isSubmitting` covers the PATCH path too.
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Publish the draft (or create directly if no draft exists).
   * Validates the full form, then PATCHes/POSTs with a finalized (non-Draft) status.
   */
  const handleCreate = useCallback(async () => {
    // Re-entrancy guard: the publishDraft (PATCH) path is not covered by
    // `isCreating` from useCreateBoat, so `isSubmitting` is the single
    // in-flight sentinel for both paths.
    if (isSubmitting) return;

    const values = methods.getValues();
    const result = fullWizardSchema.safeParse(values);

    if (!result.success) {
      const seen = new Set<string>();
      const fieldErrors: string[] = [];
      for (const issue of result.error.issues) {
        const path = issue.path.join('.') as keyof WizardFormData;
        methods.setError(path, { message: issue.message });
        const field = String(issue.path[0]);
        if (!seen.has(field)) {
          seen.add(field);
          fieldErrors.push(WIZARD_FIELD_LABELS[field] || field);
        }
      }
      toast.error(`Please fill required fields: ${fieldErrors.join(', ')}`, { duration: 5000 });
      return;
    }

    // -------------------------------------------------------------------------
    // Finalize internal status — NEVER publish a boat as Draft.
    //
    // The canvas form is hydrated from the server boat, so values.status_id is
    // the Draft status id when the boat was created via ensureDraft(). If the
    // dealer explicitly chose a non-draft status in the Status dropdown we must
    // respect that choice. Rules:
    //   • Empty or Draft status_id  → force to Available (default finalized state)
    //   • Any other status_id       → keep as-is (dealer chose e.g. In Stock)
    //
    // Fail-safe: if the statuses catalog hasn't loaded yet (availableStatusId is
    // null), block finalize with a clear message rather than silently re-saving
    // as Draft and suppressing BoatPublished + all downstream enrichment.
    // -------------------------------------------------------------------------
    if (!availableStatusId) {
      toast.error('Status options not loaded yet — please wait a moment and try again.');
      return;
    }
    const isDraftOrEmpty = !values.status_id || values.status_id === draftStatusId;
    const finalStatusId = isDraftOrEmpty ? availableStatusId : values.status_id;

    setIsSubmitting(true);
    try {
      // E5c — persist any genuinely-new evolving-field values (fuel_type /
      // engine_type) as provisional (needs_review) catalog terms before the
      // boat write, so the values resolve cleanly later. Best-effort: never let
      // a term-create failure abort the boat save.
      try {
        await createPendingVocabularyTerms(values as unknown as Record<string, unknown>);
      } catch (e) {
        console.error('E5c: pending vocabulary term creation failed', e);
      }
      if (draftSync.hasServerDraft) {
        // #1177 (spec §3.1) — finalize sends status_id plus the dealer's changes,
        // never the whole form; BoatPublished still fires once on leaving Draft.
        // #607 — creation never publishes to the website; visibility is a separate
        // action from the boat's Quick Actions panel.
        const outcome = await saveChanges({
          extra: { status_id: finalStatusId, website_status: 'disabled' },
        });
        // A conflict (dialog), a 422 (on the fields) or a stale boat (toast) was
        // surfaced by the save itself; the canvas stays open.
        if (outcome?.kind !== 'saved') return;
        const createdBoatId = draftSync.boatId;
        mediaUpload.clear();
        draftSync.resetDraft();
        setAutoSaveEnabled(false);
        toast.success('Boat created successfully!');
        onOpenChange(false);
        if (createdBoatId) {
          onSuccess?.(createdBoatId);
        }
      } else {
        // Fallback: direct POST if no server draft exists. Use the returned
        // id since draftSync.boatId is null on this path.
        //
        // Note: `loadExisting` (called in the removed AddBoatWizard's handleCreateDraft to
        // re-hydrate promoted media) is intentionally omitted here because the
        // canvas unmounts on success — there are no subsequent media operations
        // that would need the refreshed image ids.
        const payload = buildBoatPayload({ ...values, status_id: finalStatusId }, canViewCost);
        const created = await createBoat(payload as BoatFormData);
        const createdBoatId = String(created.id ?? '');
        mediaUpload.clear();
        draftSync.resetDraft();
        setAutoSaveEnabled(false);
        toast.success('Boat created successfully!');
        onOpenChange(false);
        if (createdBoatId) {
          onSuccess?.(createdBoatId);
        }
      }
    } catch (error) {
      toast.error('Failed to create boat. Please try again.');
      console.error('Create boat error:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, methods, draftSync, mediaUpload, createBoat, createPendingVocabularyTerms, onOpenChange, onSuccess, setAutoSaveEnabled, saveChanges, availableStatusId, draftStatusId, canViewCost]);

  /**
   * Save Draft handler — also bootstraps the server draft when none exists.
   * Validates the 4 basics (condition / manufacturer / model / model_year)
   * then either creates or updates the server draft.
   */
  const handleSaveDraft = useCallback(async () => {
    if (draftSync.hasServerDraft) {
      // Intentional: NO validation on the existing-draft branch.
      // "Save draft" on a canvas is a "save-what-you-have" flush — drafts are
      // incomplete by definition and the user should never be blocked from
      // persisting partial work. Contrast with the no-draft branch below which
      // validates the 4 basics before bootstrapping a new server record.
      try {
        // #1177 — only the dealer's changes, with the version. A rejected save
        // (conflict dialog, field errors, stale toast) is not "saved".
        const outcome = await saveChanges();
        if (outcome?.kind === 'saved' || outcome?.kind === 'nothing_to_save') {
          toast.success('Draft saved');
        }
      } catch {
        toast.error('Failed to save draft. Please try again.');
      }
      return;
    }

    // No draft yet — validate basics first, then create
    const isValid = await methods.trigger(['condition', 'manufacturer', 'model', 'model_year']);
    if (!isValid) {
      toast.error('Please fill in the required fields before saving');
      return;
    }

    const values = methods.getValues();
    try {
      const result = await draftSync.createDraft({
        condition: values.condition,
        manufacturer: values.manufacturer,
        model: values.model,
        model_year: values.model_year,
      });

      // Re-hydrate the committed gallery from the server response. The canvas
      // stays mounted after Save-draft (unlike the direct-Create path which
      // unmounts), so the gallery must reflect the persisted images and
      // primary image.
      if (result?.images && result.images.length > 0) {
        mediaUpload.loadExisting(result.images, result.primary_image);
      }

      setAutoSaveEnabled(true);
      toast.success('Draft saved');
    } catch {
      toast.error('Failed to save draft. Please try again.');
    }
  }, [methods, draftSync, mediaUpload, setAutoSaveEnabled, saveChanges]);

  return { handleCreate, handleSaveDraft, isSubmitting };
}
